import React, { useCallback, useEffect, useRef, useState } from 'react';
import EvaluacionesLista from './EvaluacionesLista';
import EvaluacionNotasGrid from './EvaluacionNotasGrid';
import CursoResumenAcademico from './CursoResumenAcademico';
import EvaluacionesConfirm from './EvaluacionesConfirm';
import './Evaluaciones.css';

// Evaluaciones de un curso: lista de instancias, planilla de notas y resumen académico.
// Sirve para colegios (con selector de materia) y particulares; "api" sale de services/EvaluacionesApi.js.
// soloResumen: solo la matriz de notas (lectura), por ejemplo para el preceptor.
const EvaluacionesCursoModal = ({ api, cursoNombre, initialView = 'lista', onClose, onChanged, soloResumen = false }) => {
  const [view, setView] = useState(soloResumen ? 'resumen' : initialView);
  const [materias, setMaterias] = useState([]);
  const [materiaId, setMateriaId] = useState('');
  const [materiasLoading, setMateriasLoading] = useState(api.usaMaterias);
  const [materiasError, setMateriasError] = useState('');
  const [evaluacionId, setEvaluacionId] = useState('');
  const [closeConfirm, setCloseConfirm] = useState(null);
  const [closing, setClosing] = useState(false);
  const gridRef = useRef(null);
  const changedRef = useRef(false);

  useEffect(() => {
    if (!api.usaMaterias || soloResumen) return undefined;
    let active = true;
    setMateriasLoading(true);
    api.listarMaterias()
      .then((items) => {
        if (!active) return;
        setMaterias(items);
        setMateriaId(items.length === 1 ? items[0].idCursoMateria : '');
        setMateriasError('');
      })
      .catch((err) => active && setMateriasError(err?.message || 'No pudimos cargar las materias del curso.'))
      .finally(() => active && setMateriasLoading(false));
    return () => {
      active = false;
    };
  }, [api, soloResumen]);

  const markChanged = useCallback(() => {
    changedRef.current = true;
  }, []);

  const finishClose = useCallback(() => {
    if (changedRef.current) onChanged?.();
    onClose();
  }, [onChanged, onClose]);

  // Antes de salir de la planilla, guarda lo pendiente; si algo no se pudo guardar, pregunta.
  const leavePlanilla = useCallback(async (next) => {
    if (view !== 'planilla' || !gridRef.current) {
      next();
      return;
    }
    setClosing(true);
    const { pendientes } = await gridRef.current.flush();
    setClosing(false);
    if (pendientes > 0) {
      setCloseConfirm({ pendientes, next });
      return;
    }
    next();
  }, [view]);

  const handleClose = () => leavePlanilla(finishClose);

  const goTo = (nextView) => leavePlanilla(() => {
    setView(nextView);
    if (nextView !== 'planilla') setEvaluacionId('');
  });

  const necesitaMateria = api.usaMaterias && !materiaId;
  const materiaNombre = materias.find((materia) => materia.idCursoMateria === materiaId)?.nombre || '';

  return (
    <div className="ev-overlay">
      <div className="ev-modal" role="dialog" aria-modal="true" aria-label={`Evaluaciones de ${cursoNombre}`}>
        <header className="ev-modal-header">
          <div>
            <span className="ev-eyebrow">Evaluaciones</span>
            <h3>{cursoNombre}{materiaNombre && view !== 'resumen' ? ` · ${materiaNombre}` : ''}</h3>
          </div>
          {!soloResumen && (
          <div className="ev-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={view !== 'resumen'}
              className={view !== 'resumen' ? 'active' : ''}
              onClick={() => view === 'resumen' && goTo('lista')}
            >
              <i className="fas fa-clipboard-list" aria-hidden="true" />
              Instancias y notas
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'resumen'}
              className={view === 'resumen' ? 'active' : ''}
              onClick={() => goTo('resumen')}
            >
              <i className="fas fa-chart-bar" aria-hidden="true" />
              Resumen académico
            </button>
          </div>
          )}
          <button type="button" className="ev-icon-btn" onClick={handleClose} title="Cerrar" disabled={closing}>
            <i className={`fas ${closing ? 'fa-spinner fa-spin' : 'fa-times'}`} aria-hidden="true" />
          </button>
        </header>

        <div className="ev-modal-body">
          {api.usaMaterias && view !== 'resumen' && (
            <div className="ev-materia-bar">
              <label htmlFor="ev-materia-select">Materia</label>
              {materiasLoading ? (
                <span className="ev-muted"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Cargando materias...</span>
              ) : materias.length === 0 ? (
                <span className="ev-muted">{materiasError || 'No tenés materias asignadas en este curso.'}</span>
              ) : (
                <select
                  id="ev-materia-select"
                  value={materiaId}
                  onChange={(event) => {
                    setMateriaId(event.target.value);
                    setView('lista');
                  }}
                  disabled={view === 'planilla'}
                >
                  <option value="">Elegí una materia...</option>
                  {materias.map((materia) => (
                    <option key={materia.idCursoMateria} value={materia.idCursoMateria}>{materia.nombre}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {view === 'resumen' ? (
            <CursoResumenAcademico api={api} cursoNombre={cursoNombre} />
          ) : necesitaMateria ? (
            !materiasLoading && materias.length > 0 && (
              <div className="ev-empty">
                <i className="fas fa-book" aria-hidden="true" />
                <p>Elegí la materia para ver y cargar sus evaluaciones.</p>
              </div>
            )
          ) : view === 'planilla' && evaluacionId ? (
            <EvaluacionNotasGrid
              ref={gridRef}
              api={api}
              evaluacionId={evaluacionId}
              onBack={() => goTo('lista')}
              onSaved={markChanged}
            />
          ) : (
            <EvaluacionesLista
              key={materiaId || 'curso'}
              api={api}
              idCursoMateria={materiaId || null}
              onCalificar={(evaluacion) => {
                setEvaluacionId(evaluacion.id);
                setView('planilla');
              }}
              onChanged={markChanged}
            />
          )}
        </div>
      </div>

      {closeConfirm && (
        <EvaluacionesConfirm
          title="Hay notas sin guardar"
          confirmLabel="Salir igual"
          danger
          onCancel={() => setCloseConfirm(null)}
          onConfirm={() => {
            const { next } = closeConfirm;
            setCloseConfirm(null);
            next();
          }}
        >
          {closeConfirm.pendientes === 1 ? 'Una nota no se pudo guardar' : `${closeConfirm.pendientes} notas no se pudieron guardar`}
          {' '}(formato inválido o error de conexión). Si salís, se pierden esos cambios.
        </EvaluacionesConfirm>
      )}
    </div>
  );
};

export default EvaluacionesCursoModal;
